import type { AnalyticsAdapter } from '../adapters/types';

import { publishConsentDefault } from '../adapters/consent-mode-bootstrap';
import { createConsoleAdapter } from '../adapters/console-adapter';
import { createFirstPartyAdapter } from '../adapters/first-party-adapter';
import { createGoogleAnalyticsAdapter } from '../adapters/google-analytics-adapter';
import { createGtmDataLayerAdapter } from '../adapters/gtm-data-layer-adapter';
import { createPostHogAdapter } from '../adapters/posthog-adapter';
import { createSegmentAdapter } from '../adapters/segment-adapter';
import { AnalyticsClient } from '../analytics-client';
import { deriveSide } from './derive-side';

interface CreateAnalyticsClientArgs {
  env: {
    POSTHOG_API_KEY: string;
    GOOGLE_ANALYTICS_MEASUREMENT_ID: string;
    GOOGLE_TAG_MANAGER_CONTAINER_ID: string;
    SEGMENT_WRITE_KEY: string;
    FIRST_PARTY_ANALYTICS_WRITE_KEY: string;
    FIRST_PARTY_ANALYTICS_HOST: string;
    ENVIRONMENT: string;
  };
  /**
   * Called each time `route()` needs the current user. Called per event,
   * not memoised, so it should be cheap — React callers typically close
   * over a ref. Structural shape so this file doesn't depend on the full
   * `User` type from session.
   */
  getUser: () => { roles: string[] } | null;
  /** Called each time `route()` needs the current pathname. Same rules. */
  getPathname: () => string;
}

/**
 * Build a fully-wired `AnalyticsClient`:
 *
 * 1. Calls each adapter module's factory with the supplied env, collecting
 *    the non-null results.
 * 2. Falls back to a console adapter when nothing activated (so local dev
 *    without env vars still echoes events).
 * 3. Binds the routing context (`side`) to the supplied user/pathname
 *    accessors so it always sees the latest value at dispatch time.
 *
 * Pure function: takes inputs, returns a client. No React inside —
 * callers (the provider, tests) pass accessor closures over whatever
 * source they use to track the latest user/pathname.
 */
export function createAnalyticsClient(
  args: CreateAnalyticsClientArgs,
): AnalyticsClient {
  const adapters = buildAdapters(args.env);
  return new AnalyticsClient(adapters, () => ({
    side: deriveSide(args.getUser(), args.getPathname()),
  }));
}

/**
 * Module-scoped cache so React Strict Mode's intentional double-mount in
 * dev doesn't construct the AnalyticsClient (and its adapters'
 * side-effectful constructors — gtag setup, script injection, etc.) twice.
 * The provider calls this via a `useRef` lazy-init pattern; the cache makes
 * the second mount's call a no-op.
 *
 * The cache is keyed on the env values, so a hypothetical env change
 * (e.g. a future hot-reload scenario) would build a fresh client. In
 * practice env is static for the lifetime of the page.
 */
let cached: { key: string; client: AnalyticsClient } | null = null;

/**
 * Latest user/pathname accessors handed in by the most recent
 * `getOrCreateAnalyticsClient` call. The cached client's `getContext`
 * reads through this holder rather than closing over a specific
 * provider instance's refs, so a remounted provider (dev HMR, error
 * boundary recovery) doesn't leave the cached client staring at the
 * unmounted instance's orphaned refs.
 */
let latestAccessors: {
  getUser: CreateAnalyticsClientArgs['getUser'];
  getPathname: CreateAnalyticsClientArgs['getPathname'];
} | null = null;

export function getOrCreateAnalyticsClient(
  args: CreateAnalyticsClientArgs,
): AnalyticsClient {
  // ALWAYS update the holder, even on cache hit — that's the whole
  // point of the indirection. A new provider mount carries new
  // `userRef`/router closures; the cached client must see those, not
  // the ones captured when the cache was warmed.
  latestAccessors = {
    getUser: args.getUser,
    getPathname: args.getPathname,
  };

  const key = JSON.stringify(args.env);
  if (cached?.key !== key) {
    const adapters = buildAdapters(args.env);
    const client = new AnalyticsClient(adapters, () => ({
      // Safe to read directly: `getOrCreateAnalyticsClient` always
      // sets latestAccessors before constructing the client, and the
      // reset hook only runs in tests.
      side: latestAccessors
        ? deriveSide(latestAccessors.getUser(), latestAccessors.getPathname())
        : 'unknown',
    }));
    cached = { key, client };
  }
  return cached.client;
}

/** Test-only reset hook — clears the module cache and the accessor holder. */
export function __resetAnalyticsClientCacheForTesting() {
  cached = null;
  latestAccessors = null;
}

function buildAdapters(
  env: CreateAnalyticsClientArgs['env'],
): AnalyticsAdapter[] {
  // Consent Mode state goes on the page before any factory runs, and this
  // is the call that makes it unconditional: whatever the env says, a
  // browser that got this far has a consent default published. The two
  // things that inject a Google script — `bootstrapGtag`,
  // `loadGtmContainer` — publish it themselves too, so no adapter's
  // guarantee rests on this line or on the order below. It covers the case
  // neither of them can: a container that reached the page from somewhere
  // other than our loader.
  //
  // Nothing below may depend on this having worked, so a throw must not
  // cost the page its adapters.
  try {
    publishConsentDefault();
  } catch (error) {
    console.error('Analytics consent default error', error);
  }

  const adapters = [
    createPostHogAdapter(env),
    createGoogleAnalyticsAdapter(env),
    createGtmDataLayerAdapter(env),
    createSegmentAdapter(env),
    createFirstPartyAdapter(env),
  ].filter((a): a is AnalyticsAdapter => a !== null);
  if (adapters.length === 0) adapters.push(createConsoleAdapter());
  return adapters;
}
